import React, { useEffect, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { SelectDateRange } from "components/forms";
import { useActionHook, usePrompt } from "hooks";
import { capitalize } from "lodash";
import { useReportsActions } from "store";
import { DateRangeValue, ReportFilters } from "store/types";

import { currenciesArray } from "../../../constants";
import { State } from "./CreateReportSection";

const toCapitalizeList: Array<string> = ["round", "deal_name"]; //added after capitalize .replace(/_/g," ") in case server changed key of filter Deal Name (replaced with deal_name)

interface Props {
  data: State["queries"];
  setData: (cb: (prevState: State["queries"]) => State["queries"]) => void;
  lockedFilters?: ReportFilters;
}

export const Queries: React.FC<Props> = ({ data, setData, lockedFilters = {} }) => {
  const { dateRange, filters, currency, exchangeMethod } = data;

  const { getFilters } = useReportsActions();
  const openPrompt = usePrompt();

  const { data: filtersList, action: getFiltersAction, loading: getFiltersLoading } = useActionHook(getFilters);

  useEffect(() => {
    getFiltersAction({});
  }, []);

  const handleDateRangeChange = (value: DateRangeValue) => {
    setData((prevState) => ({ ...prevState, dateRange: value }));
  };

  const removeFilter = (filter: string): void => {
    const filtersCopy = { ...filters };
    delete filtersCopy[filter];
    setData((prevState) => {
      const newFilters = { ...prevState.filters };
      delete newFilters[filter];
      getFiltersAction(newFilters);
      return { ...prevState, filters: newFilters };
    });
  };

  const updateSpecificFilter = async (data: { filter: string; e: React.MouseEvent<HTMLDivElement, MouseEvent> }) => {
    const { filter, e } = data;

    const newFiltersSet = await openPrompt({
      Component: FiltersPicker,
      componentType: "popover",
      anchorEl: e.currentTarget,
      props: {
        options: (filtersList?.[filter] as string[]) || [],
        initialValue: (filters[filter] as string[]) || []
      }
    });

    if (!newFiltersSet) return;

    setData((prevState) => {
      const newFilters = { ...prevState.filters, [filter]: newFiltersSet };
      getFiltersAction(newFilters);
      return { ...prevState, filters: newFilters };
    });
  };

  const manageFiltersList = async (e: React.MouseEvent<HTMLButtonElement>) => {
    // Filter out locked filters from available options
    const availableFilters = Object.keys(filtersList || {}).filter(
      (filter) => !lockedFilters || !lockedFilters[filter]
    );

    // Only include non-locked filters in initial value
    const nonLockedFilters = Object.keys(filters).filter((filter) => !lockedFilters || !lockedFilters[filter]);

    const newFiltersSet = await openPrompt({
      Component: FiltersPicker,
      componentType: "popover",
      anchorEl: e.currentTarget,
      props: {
        options: availableFilters,
        initialValue: nonLockedFilters,
        lockedFilters
      }
    });

    if (!newFiltersSet) return;

    const result = { ...filters };

    // Remove non-locked filters if they were unselected
    Object.keys(result).forEach((filter) => {
      if (lockedFilters && lockedFilters[filter]) return; // Don't remove locked filters
      if (newFiltersSet.includes(filter)) return;
      delete result[filter];
    });

    // Copy selected filter to newly selected
    newFiltersSet.forEach((filter) => {
      result[filter] = result[filter] || [];
    });

    setData((prevState) => ({ ...prevState, filters: result }));
    getFiltersAction(result);
  };

  const handleClearAll = () => {
    // Preserve locked filters when clearing all
    const clearedFilters = lockedFilters ? { ...lockedFilters } : {};
    setData((prevState) => ({ ...prevState, filters: clearedFilters, currency: "USD" }));
    getFiltersAction(clearedFilters);
  };

  return (
    <>
      <Stack sx={{ gap: "10px", minWidth: 360 }}>
        <Stack sx={{ flexDirection: "row", gap: "10px" }}>
          <SelectDateRange
            formControlProps={{ sx: { minWidth: "200px" } }}
            selectProps={{
              startAdornment: <TodayOutlinedIcon sx={{ fontSize: "15px" }} />,
              sx: { borderRadius: "8px", height: "40px !important" }
            }}
            label="Time Frame"
            value={dateRange}
            onChange={handleDateRangeChange}
          />
          <Autocomplete
            value={currency}
            fullWidth
            options={currenciesArray}
            disableClearable
            onChange={(_, value) => setData((prevState) => ({ ...prevState, currency: value }))}
            renderInput={(params) => <TextField {...params} label="Currency" />}
          />
        </Stack>
        <FormControl sx={{ flexDirection: "row", alignItems: "center" }}>
          <FormLabel id="demo-controlled-radio-buttons-group" sx={{ marginRight: "25px" }}>
            Hedging:
          </FormLabel>
          <RadioGroup
            sx={{ flexDirection: "row" }}
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={exchangeMethod}
            onChange={(e) => {
              setData((prevState) => ({ ...prevState, exchangeMethod: e.target.value as "toDate" | "realDate" }));
            }}
          >
            <FormControlLabel value="toDate" control={<Radio />} label="To Date" />
            <FormControlLabel value="realDate" control={<Radio />} label="Transaction Date" />
          </RadioGroup>
        </FormControl>
        <Typography sx={{ fontSize: "16px", fontWeight: 400, lineHeight: "24px" }}>Filters</Typography>
        <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
          {Object.entries(filters).map(([filter, values]) => {
            const isLocked = lockedFilters && lockedFilters[filter];
            return (
              <Chip
                disabled={getFiltersLoading}
                key={filter}
                sx={{
                  height: "26px",
                  fontSize: "15px",
                  color: isLocked ? "#666" : "#0a3142",
                  backgroundColor: isLocked ? "#f5f5f5" : "transparent",
                  border: isLocked ? "1px solid #ddd" : "none"
                }}
                label={`${toCapitalizeList.includes(filter) ? capitalize(filter).replace(/_/g, " ") : filter} (${
                  values.length
                })`}
                onDelete={isLocked ? undefined : () => removeFilter(filter)}
                onClick={isLocked ? undefined : (e) => updateSpecificFilter({ filter, e })}
              />
            );
          })}
          {getFiltersLoading ? (
            <Stack sx={{ alignItems: "center", width: 100 }}>
              <CircularProgress size={20} />
            </Stack>
          ) : (
            <Button
              size="sm"
              sx={{
                color: "#0a3142",
                p: "0 10px",
                minWidth: 0,
                fontSize: "15px"
              }}
              variant="text"
              onClick={manageFiltersList}
              startIcon={<AddIcon />}
            >
              Add Filter
            </Button>
          )}
        </Stack>
        <Button size="sm" sx={{ alignSelf: "flex-start", color: "#26A6AE" }} onClick={handleClearAll}>
          Clear All
        </Button>
      </Stack>
    </>
  );
};

interface FiltersPickerProps {
  options: string[];
  initialValue: string[];
  onSubmit: (value: string[]) => void;
  onCancel: () => void;
  lockedFilters?: ReportFilters;
}

const FiltersPicker: React.FC<FiltersPickerProps> = ({
  options,
  initialValue,
  onSubmit,
  onCancel,
  lockedFilters = {}
}) => {
  // Include locked filters in the initial state
  const lockedFilterKeys = Object.keys(lockedFilters);
  const lockedFilterDisplayNames = lockedFilterKeys.map((key) => {
    if (key === "deal_name") return "Deal Name";
    return key;
  });

  const initialState = Array.from(new Set([...initialValue, ...lockedFilterDisplayNames]));
  const [state, setState] = useState<string[]>(initialState);
  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);

  return (
    <Stack sx={{ alignItems: "stretch", p: "30px", gap: "30px", maxWidth: "500px" }}>
      <Stack
        sx={{ borderBottom: "1px solid #dae0e2", pb: "5px" }}
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography sx={{ fontSize: "15px" }}>Add Filter</Typography>
        <Stack flexDirection="row">
          <Button sx={{ color: "#26A6AE" }} variant="text" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            sx={{ color: "#26A6AE", ":disabled": { backgroundColor: "#FFF", color: "#bac7ce" } }}
            variant="text"
            onClick={() => onSubmit(state)}
          >
            Save
          </Button>
        </Stack>
      </Stack>
      <Autocomplete
        multiple
        limitTags={1}
        disableClearable
        disableCloseOnSelect
        renderInput={(params) => <TextField {...params} label="Filters" inputRef={inputRef} />}
        options={[
          "SELECT_ALL",
          ...options.map((item) => {
            if (item === "deal_name") {
              return "Deal Name";
            }
            return item;
          })
        ]}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option) => {
          // Check if this is the Select All option
          if (option === "SELECT_ALL") {
            const allOptions = options.map((item) => {
              if (item === "deal_name") {
                return "Deal Name";
              }
              return item;
            });
            const isAllSelected =
              allOptions.length === state.length && allOptions.every((option) => state.includes(option));

            return (
              <li {...props} key="select-all" style={{ borderBottom: "1px solid #dae0e2", padding: "8px 16px" }}>
                <div
                  style={{
                    color: "#26A6AE",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: "pointer",
                    width: "100%"
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isAllSelected) {
                      // Unselect all
                      setState([]);
                    } else {
                      // Select all
                      setState(allOptions);
                    }
                  }}
                >
                  {isAllSelected ? "Unselect All" : "Select All"}
                </div>
              </li>
            );
          }

          const isLocked = lockedFilters && lockedFilters[option];
          return (
            <li
              {...props}
              key={option}
              style={{
                ...props.style,
                opacity: isLocked ? 0.6 : 1,
                cursor: isLocked ? "not-allowed" : "default"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span>{toCapitalizeList.includes(option) ? capitalize(option).replace(/_/g, " ") : option}</span>
                {isLocked && <span style={{ fontSize: "12px", color: "#999", fontStyle: "italic" }}>(Locked)</span>}
              </div>
            </li>
          );
        }}
        renderTags={(tagValue, getTagProps) => {
          return (
            <div style={{ maxHeight: "160px", overflowY: "auto", overflowX: "hidden" }}>
              {tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={index}
                  label={toCapitalizeList?.includes(option) ? capitalize(option).replace(/_/g, " ") : option}
                />
              ))}
            </div>
          );
        }}
        sx={{ maxWidth: "300px", minWidth: "300px" }}
        value={state.map((item) => {
          if (item === "deal_name") {
            return "Deal Name";
          }
          return item;
        })}
        onChange={(e, value) => {
          // Filter out the SELECT_ALL option from the final value
          const filteredValue = value.filter((item) => item !== "SELECT_ALL");

          // Ensure locked filters are always included
          const lockedFilterKeys = Object.keys(lockedFilters);
          const lockedFilterDisplayNames = lockedFilterKeys.map((key) => {
            if (key === "deal_name") return "Deal Name";
            return key;
          });

          // Add locked filters to the value if they're not already there
          const finalValue = Array.from(new Set([...filteredValue, ...lockedFilterDisplayNames]));

          setState(
            finalValue.map((item) => {
              if (item === "Deal Name") {
                return "deal_name";
              }
              return item;
            })
          );
        }}
      />
    </Stack>
  );
};
